import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

import { DRIVE_URL_HINT, googleDriveIdFromUrl } from "./drive-url.ts";

// Requestors type the item id to name the drive, including in the CLI, so it is
// kept to characters that need no quoting in a shell.
const SHARED_DRIVE_ID_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const SHARED_DRIVE_ID_MAX_LENGTH = 63;
const SHARED_DRIVE_ID_HINT =
  "Use lowercase letters, digits, and hyphens only, starting and ending with " +
  `a letter or digit, up to ${SHARED_DRIVE_ID_MAX_LENGTH} characters.`;

/** Two items, and every artifact the install produces has exactly one owner:
 * the Cloud Run service, its service account and its invoker binding belong to
 * `connector`; the one Drive ACL entry making P0's admin identity a member of a
 * drive belongs to `shared-drive`. The identities P0 grants to belong to neither
 * — they are minted by the GCP Service Account Creator integration, in the
 * connector's own project.
 */
export const GoogleDriveAiComponents = InstallSpec({
  connector: ItemComponent({
    label: "Drive connector",
    description:
      "Runs P0's Google Drive connector in your Google Cloud project",
    // The item is identified by the Google Cloud project the connector runs in,
    // which is also the project P0 mints agentic identities in. It is picked
    // from the installed GCP Service Account Creator projects, each of which is
    // already a gcloud iam-write project, so P0 can reach Cloud Run there too.
    type: "dynamic",
    // No `maxItems`: a customer can run a connector in each of several
    // projects, and each shared drive names the one that manages it.
    maturity: "beta",
    schema: {
      connectorServiceName: Element({
        label: "Cloud Run service name",
        description: "The Cloud Run service hosting the connector",
        type: "generated",
      }),
      // Customer-facing, unlike its `gcp-cloudsql` counterpart: this is the
      // address a drive manager pastes into each shared drive's sharing dialog.
      connectorServiceAccount: Element({
        label: "Connector service account",
        description:
          "The connector's service account. Add it as a Manager on each shared drive.",
        type: "generated",
      }),
      connectorServiceUri: Element({
        label: "Cloud Run service URI",
        description: "The connector's Cloud Run invocation URL",
        type: "generated",
      }),
      connectorRegion: Element({
        label: "Region",
        description: "The Google Cloud region the connector runs in",
        type: "hidden",
        step: "new",
      }),
    },
  }),
  "shared-drive": ItemComponent({
    label: "Shared drive",
    description: "A Google shared drive P0 can grant access in",
    // A name the customer chooses, so requests, rules and the CLI name the
    // drive by something readable rather than its opaque Drive ID.
    type: "string",
    maturity: "beta",
    validator: async (id) =>
      id.length <= SHARED_DRIVE_ID_MAX_LENGTH &&
      SHARED_DRIVE_ID_PATTERN.test(id)
        ? undefined
        : SHARED_DRIVE_ID_HINT,
    schema: {
      // "new" because `googleDriveId` is derived from it once, when the item is
      // created; a later edit would leave the two disagreeing.
      googleDriveUrl: Element({
        label: "Google shared drive URL",
        description:
          "The shared drive's URL, copied from the browser with the drive open",
        type: "string",
        placeholder: "https://drive.google.com/drive/folders/…",
        step: "new",
        validator: async (_id, field) =>
          googleDriveIdFromUrl(field) ? undefined : DRIVE_URL_HINT,
      }),
      googleDriveId: Element({
        label: "Google shared drive ID",
        description: "Google's ID for the shared drive, read from its URL",
        type: "generated",
      }),
      // A connector item's id: the project the Drive connector runs in, which
      // is also where P0 creates the service accounts it grants to. Framed as
      // a project because that is what the customer is choosing.
      //
      // "new" because the stage instruction — which admin address to share this
      // drive with — is rendered from it, and that instruction is the stage
      // screen.
      projectId: Element({
        label: "Service account project",
        description:
          "The Google Cloud project that P0 creates this drive's service accounts in. Requires the Google Drive connector to be installed in this project.",
        type: "dynamic",
        step: "new",
      }),
    },
  }),
});
